// SimilarProducts.tsx
import { useEffect, useState } from 'react';
import Swiper from 'components/base/Swiper';
import { SwiperSlide } from 'swiper/react';
import Button from 'components/base/Button';
import ProductCardCat from 'components/common/ProductCardCat';
import { Link } from 'react-router-dom';
import type { ProductCat } from 'data/e-commerce/products';
import {
  getAllProducts,
  getAllProductsBySubcategory
} from 'api/products/_requests';
import { PF } from 'helpers/utils';

interface Props {
  productId?: string;
  subCategoryId: string;
}

const SimilarProducts: React.FC<Props> = ({ subCategoryId, productId }) => {
  const [productsCat, setProductsCat] = useState<ProductCat[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const products = await getAllProductsBySubcategory(subCategoryId);

        if (cancelled) return;

        const transformed: ProductCat[] =
          products
            ?.filter((item: { _id: string }) => item._id !== productId)
            .map(
              (item: {
                _id: string;
                slug: string;
                thumbnail?: string;
                name: string;
                price: number;
                discountedPrice: number;
                vendorId: string;
                productId: string;
              }) => ({
                _id: item._id,
                thumbnail: item.thumbnail ?? 'goods', // fallback image
                name: item.name,
                slug: item.slug,
                price: item.price,
                salePrice: item.discountedPrice,
                vendorId: item.vendorId,
                productId: item.productId
              })
            ) ?? [];

        setProductsCat(transformed);
      } catch (err) {
        console.error('SimilarProducts fetch error', err);
      } finally {
        !cancelled && setLoading(false);
      }
    })();

    // cleanup so we don’t set state on an unmounted component
    return () => {
      cancelled = true;
    };
  }, [productId, subCategoryId]);

  if (loading) {
    return <p className="text-center my-3">Loading similar products…</p>;
  }

  if (!productsCat.length) {
    return null; // nothing similar to show
  }

  return (
    <>
      {/* tiny scoped styles */}
      <style>{`
        .sim-swiper .swiper-slide { padding: 0.25rem; }
        .sim-swiper .swiper-button-next,
        .sim-swiper .swiper-button-prev {
          color: var(--bs-body-color); opacity: .75; transition: opacity .15s;
        }
        .sim-swiper .swiper-button-next:hover,
        .sim-swiper .swiper-button-prev:hover { opacity: 1; }
      `}</style>

      {/* header */}
      <div className="d-flex flex-between-center mb-3 px-1 px-md-2">
        <div>
          <h3 className="mb-1">Similar Products</h3>
          <p className="mb-0 text-body-tertiary fw-semibold">
            Essential for a better life
          </p>
        </div>
        <Link to="#" className="text-decoration-none">
          <Button size="sm" variant="phoenix-primary">
            View all
          </Button>
        </Link>
      </div>

      {/* swiper */}
      <Swiper
        className="sim-swiper"
        spaceBetween={16}
        slidesPerView={1}
        navigationPosition={{ top: '32%' }}
        breakpoints={{
          0: { slidesPerView: 1, spaceBetween: 16 },
          450: { slidesPerView: 2, spaceBetween: 16 },
          576: { slidesPerView: 3, spaceBetween: 20 },
          768: { slidesPerView: 4, spaceBetween: 20 },
          992: { slidesPerView: 5, spaceBetween: 20 },
          1200: { slidesPerView: 5, spaceBetween: 16 }
        }}
      >
        {productsCat.map(p => (
          <SwiperSlide key={p._id}>
            <ProductCardCat product={p} setProduct={() => {}} />
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default SimilarProducts;
